import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { isListingExpired } from '#/modules/listing/helpers/isListingExpired';
import type { viewVolopReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { listingApiIsomorphicFetchVolop } from '#/modules/listing/volops/api/listingApiFetchVolop';
import { ExpiredVolopPageLoadable } from '#/modules/listing/volops/pages/expired/ExpiredVolopPageLoadable';
import { UnpublishedVolopPageLoadable } from '#/modules/listing/volops/pages/unpublished/UnpublishedVolopPageLoadable';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';

import { ViewVolopPageLoadable } from './ViewVolopPageLoadable';

export const handle = {
  defaultSearchType: 'VOLOP',
  showDesktopSearch: true,
  showSearchRefinements: true,
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
  params: { volopIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewVolopReferenceRoute>) {
  const [volopId] = volopIdDashSlug.split('-');

  try {
    const [volop] = await Promise.all([
      listingApiIsomorphicFetchVolop({ request, context })(volopId),
      // It's possible this loadable won't be used if the volop is unpublished
      // but in most cases optimistically loading it is best
      ViewVolopPageLoadable.load(),
    ]);

    const fetchedListingExtras =
      await userDashboardApiIsomorphicFetchListingExtras({
        request,
        context,
      })([volopId, volop.org?.id].filter(Boolean));

    const { saved, applicationId } = fetchedListingExtras[volopId] ?? {
      saved: false,
      applicationId: null,
    };

    const orgSaved = Boolean(
      volop.org && fetchedListingExtras[volop.org.id]?.saved,
    );

    switch (volop.type) {
      case 'VOLOP':
        return {
          type: isListingExpired(volop.expires) ? 'EXPIRED_VOLOP' : 'VOLOP',
          volop,
          applicationId,
          saved,
          orgSaved,
          handle: {
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: volop.url,
          } satisfies LayoutHandleProps,
        } as const;
      case 'UNPUBLISHED_VOLOP':
        await UnpublishedVolopPageLoadable.load();
        return {
          type: volop.type,
          unpublishedVolop: volop,
        } as const;
    }
  } catch {
    return {
      type: 'NOT_FOUND',
    } as const;
  }
}

export default function ViewVolopHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  switch (loaderData.type) {
    case 'NOT_FOUND':
      return <NotFoundPageLoadable noLayout />;
    case 'VOLOP':
      return (
        <RouteModule title={loaderData.volop.name}>
          <ViewVolopPageLoadable
            volop={loaderData.volop}
            listingApplicationId={loaderData.applicationId}
            initiallySaved={loaderData.saved}
            orgInitiallySaved={loaderData.orgSaved}
          />
        </RouteModule>
      );
    case 'EXPIRED_VOLOP':
      return (
        <RouteModule title={loaderData.volop.name}>
          <ExpiredVolopPageLoadable
            volop={loaderData.volop}
            listingApplicationId={loaderData.applicationId}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_VOLOP':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <UnpublishedVolopPageLoadable
            unpublishedVolop={loaderData.unpublishedVolop}
          />
        </RouteModule>
      );
  }
}
